// Subclass for particle containers, allows for very large arrays to be spread out

import { Random } from '@/shared/misc/math-helpers'
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  Object3D,
  ShaderMaterial,
  Vector3,
  MathUtils,
  Points
} from 'three'

import GlobalParticlesSystem, { Size, SpawnParticleOptions } from './global-particles-system'

let position = new Vector3()
let velocity = new Vector3()
let color = new Color()

export default class GPUParticleContainer extends Object3D {
  PARTICLE_COUNT = 1000
  PARTICLE_CURSOR = 0
  time = 0
  offset = 0
  count = 0
  DPR = window.devicePixelRatio
  particlesSystem: GlobalParticlesSystem
  particleUpdate = false
  particleShaderGeo: BufferGeometry
  particleShaderMat: ShaderMaterial
  particlePoints: Points | undefined

  constructor(maxParticles: number, particleSystem: GlobalParticlesSystem) {
    super()

    this.PARTICLE_COUNT = maxParticles || 100000
    this.PARTICLE_CURSOR = 0
    this.time = 0
    this.offset = 0
    this.count = 0
    this.DPR = window.devicePixelRatio
    this.particlesSystem = particleSystem
    this.particleUpdate = false
    this.particleShaderGeo = new BufferGeometry()

    // ATTRIBUTES

    this.particleShaderGeo.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(this.PARTICLE_COUNT * 3), 3).setUsage(DynamicDrawUsage)
    )
    this.particleShaderGeo.setAttribute(
      'positionStart',
      new BufferAttribute(new Float32Array(this.PARTICLE_COUNT * 3), 3).setUsage(DynamicDrawUsage)
    )
    this.particleShaderGeo.setAttribute(
      'startTime',
      new BufferAttribute(new Float32Array(this.PARTICLE_COUNT), 1).setUsage(DynamicDrawUsage)
    )
    this.particleShaderGeo.setAttribute(
      'velocity',
      new BufferAttribute(new Float32Array(this.PARTICLE_COUNT * 3), 3).setUsage(DynamicDrawUsage)
    )
    this.particleShaderGeo.setAttribute(
      'turbulence',
      new BufferAttribute(new Float32Array(this.PARTICLE_COUNT), 1).setUsage(DynamicDrawUsage)
    )
    this.particleShaderGeo.setAttribute(
      'color',
      new BufferAttribute(new Float32Array(this.PARTICLE_COUNT * 3), 3).setUsage(DynamicDrawUsage)
    )
    this.particleShaderGeo.setAttribute(
      'size',
      new BufferAttribute(new Float32Array(this.PARTICLE_COUNT), 1).setUsage(DynamicDrawUsage)
    )
    this.particleShaderGeo.setAttribute(
      'lifeTime',
      new BufferAttribute(new Float32Array(this.PARTICLE_COUNT), 1).setUsage(DynamicDrawUsage)
    )
    this.particleShaderGeo.setAttribute(
      'opacity',
      new BufferAttribute(new Float32Array(this.PARTICLE_COUNT), 1).setUsage(DynamicDrawUsage)
    )

    // material
    this.particleShaderMat = this.particlesSystem.particleShaderMat

    this.init()
  }

  spawnParticle(options: SpawnParticleOptions) {
    const positionStartAttribute = this.particleShaderGeo.getAttribute('positionStart') as BufferAttribute
    const startTimeAttribute = this.particleShaderGeo.getAttribute('startTime') as BufferAttribute
    const velocityAttribute = this.particleShaderGeo.getAttribute('velocity') as BufferAttribute
    const turbulenceAttribute = this.particleShaderGeo.getAttribute('turbulence') as BufferAttribute
    const colorAttribute = this.particleShaderGeo.getAttribute('color') as BufferAttribute
    const sizeAttribute = this.particleShaderGeo.getAttribute('size') as BufferAttribute
    const lifeTimeAttribute = this.particleShaderGeo.getAttribute('lifeTime') as BufferAttribute
    const opacityAttribute = this.particleShaderGeo.getAttribute('opacity') as BufferAttribute

    options = options || {}

    // setup reasonable default values for all arguments

    position = options.position !== undefined ? position.copy(options.position) : position.set(0, 0, 0)
    velocity = options.velocity !== undefined ? velocity.copy(options.velocity) : velocity.set(0, 0, 0)
    color = options.color !== undefined ? color.set(options.color) : color.set(0xffffff)

    const positionRandomness = options.positionRandomness !== undefined ? options.positionRandomness : 0
    const velocityRandomness = options.velocityRandomness !== undefined ? options.velocityRandomness : 0
    const colorRandomness = options.colorRandomness !== undefined ? options.colorRandomness : 1
    const turbulence = options.turbulence !== undefined ? options.turbulence : 1

    const lifetime = options.lifetime !== undefined ? options.lifetime : 5
    let size = options.size !== undefined ? options.size : 10
    const sizeRandomness = options.sizeRandomness !== undefined ? options.sizeRandomness : 0
    const smoothPosition = options.smoothPosition !== undefined ? options.smoothPosition : false

    if (this.DPR !== undefined) size *= this.DPR

    const i = this.PARTICLE_CURSOR

    // position
    positionStartAttribute.setXYZ(
      i,
      position.x + Random.next() * positionRandomness,
      position.y + Random.next() * positionRandomness,
      position.z + Random.next() * positionRandomness
    )

    if (smoothPosition === true) {
      positionStartAttribute.setXYZ(
        i,
        positionStartAttribute.array[i * 3 + 0] - velocity.x * Random.next(),
        positionStartAttribute.array[i * 3 + 1] - velocity.y * Random.next(),
        positionStartAttribute.array[i * 3 + 2] - velocity.z * Random.next()
      )
    }

    // velocity
    const maxVel = 4

    let velX = velocity.x + Random.next() * 2 * velocityRandomness
    let velY = velocity.y + Random.next() * 2 * velocityRandomness
    let velZ = velocity.z + Random.next() * 2 * velocityRandomness

    velX = MathUtils.clamp(velX, -maxVel, maxVel)
    velY = MathUtils.clamp(velY, -maxVel, maxVel)
    velZ = MathUtils.clamp(velZ, -maxVel, maxVel)
    velocityAttribute.setXYZ(i, velX, velY, velZ)

    // color

    color.r = MathUtils.clamp(color.r + Random.next() * colorRandomness, 0, 1)
    color.g = MathUtils.clamp(color.g + Random.next() * colorRandomness, 0, 1)
    color.b = MathUtils.clamp(color.b + Random.next() * colorRandomness, 0, 1)

    colorAttribute.setXYZ(i, color.r, color.g, color.b)

    // turbulence, size, lifetime and starttime

    turbulenceAttribute.setX(i, turbulence + Math.random() * turbulence)
    sizeAttribute.setX(i, size + Random.next() * sizeRandomness)
    lifeTimeAttribute.setX(i, lifetime)
    // startTimeAttribute.array[ i ] = this.time + particleSystem.random() * 2e-2;
    // startTimeAttribute.array[ i ] = this.time;
    startTimeAttribute.setX(i, options.startTime!)
    opacityAttribute.setX(i, options.opacity!)

    if (this.offset === 0) {
      this.offset = this.PARTICLE_CURSOR
    }

    // counter and cursor
    this.count++
    this.PARTICLE_CURSOR++

    if (this.PARTICLE_CURSOR >= this.PARTICLE_COUNT) {
      this.PARTICLE_CURSOR = 0
    }

    this.particleUpdate = true
  }

  init() {
    this.particlePoints = new Points(this.particleShaderGeo, this.particleShaderMat)
    this.particlePoints.renderOrder = 100000
    this.particlePoints.frustumCulled = false
    this.add(this.particlePoints)
  }

  update(time: number) {
    this.time = time
    this.particleShaderMat.uniforms.uTime.value = time

    this.geometryUpdate()
  }

  resize(size: Size) {
    this.particleShaderMat.uniforms.uScale.value = Math.min(1, size.width / 1600)
  }

  updateVelocity(time: number) {
    /*const velocityAttribute = this.particleShaderGeo.getAttribute('velocity') as BufferAttribute

    for (let index = 0; index < velocityAttribute.count; index++) {
      velocityAttribute.updateRange.count = -1
      velocityAttribute.setX(index * 3 + 1, velocityAttribute.array[index * 3 + 1] - this.particlesSystem.gravity)
    }
    velocityAttribute.needsUpdate = true
    */
  }

  geometryUpdate() {
    if (this.particleUpdate === true) {
      this.particleUpdate = false

      const positionStartAttribute = this.particleShaderGeo.getAttribute('positionStart') as BufferAttribute
      const startTimeAttribute = this.particleShaderGeo.getAttribute('startTime') as BufferAttribute
      const velocityAttribute = this.particleShaderGeo.getAttribute('velocity') as BufferAttribute
      const turbulenceAttribute = this.particleShaderGeo.getAttribute('turbulence') as BufferAttribute
      const colorAttribute = this.particleShaderGeo.getAttribute('color') as BufferAttribute
      const sizeAttribute = this.particleShaderGeo.getAttribute('size') as BufferAttribute
      const lifeTimeAttribute = this.particleShaderGeo.getAttribute('lifeTime') as BufferAttribute
      const opacityAttribute = this.particleShaderGeo.getAttribute('opacity') as BufferAttribute

      if (this.offset + this.count < this.PARTICLE_COUNT) {
        positionStartAttribute.updateRange.offset = this.offset * positionStartAttribute.itemSize
        startTimeAttribute.updateRange.offset = this.offset * startTimeAttribute.itemSize
        velocityAttribute.updateRange.offset = this.offset * velocityAttribute.itemSize
        turbulenceAttribute.updateRange.offset = this.offset * turbulenceAttribute.itemSize
        colorAttribute.updateRange.offset = this.offset * colorAttribute.itemSize
        sizeAttribute.updateRange.offset = this.offset * sizeAttribute.itemSize
        lifeTimeAttribute.updateRange.offset = this.offset * lifeTimeAttribute.itemSize
        opacityAttribute.updateRange.offset = this.offset * opacityAttribute.itemSize

        positionStartAttribute.updateRange.count = this.count * positionStartAttribute.itemSize
        startTimeAttribute.updateRange.count = this.count * startTimeAttribute.itemSize
        velocityAttribute.updateRange.count = this.count * velocityAttribute.itemSize
        turbulenceAttribute.updateRange.count = this.count * turbulenceAttribute.itemSize
        colorAttribute.updateRange.count = this.count * colorAttribute.itemSize
        sizeAttribute.updateRange.count = this.count * sizeAttribute.itemSize
        lifeTimeAttribute.updateRange.count = this.count * lifeTimeAttribute.itemSize
        opacityAttribute.updateRange.count = this.count * opacityAttribute.itemSize
        opacityAttribute.updateRange.count = this.count * opacityAttribute.itemSize
      } else {
        positionStartAttribute.updateRange.offset = 0
        startTimeAttribute.updateRange.offset = 0
        velocityAttribute.updateRange.offset = 0
        turbulenceAttribute.updateRange.offset = 0
        colorAttribute.updateRange.offset = 0
        sizeAttribute.updateRange.offset = 0
        lifeTimeAttribute.updateRange.offset = 0
        opacityAttribute.updateRange.offset = 0

        // Use -1 to update the entire buffer, see #11476
        positionStartAttribute.updateRange.count = -1
        startTimeAttribute.updateRange.count = -1
        velocityAttribute.updateRange.count = -1
        turbulenceAttribute.updateRange.count = -1
        colorAttribute.updateRange.count = -1
        sizeAttribute.updateRange.count = -1
        lifeTimeAttribute.updateRange.count = -1
        opacityAttribute.updateRange.count = -1
      }

      positionStartAttribute.needsUpdate = true
      startTimeAttribute.needsUpdate = true
      velocityAttribute.needsUpdate = true
      turbulenceAttribute.needsUpdate = true
      colorAttribute.needsUpdate = true
      sizeAttribute.needsUpdate = true
      lifeTimeAttribute.needsUpdate = true
      opacityAttribute.needsUpdate = true

      this.offset = 0
      this.count = 0
    }
  }

  dispose() {
    this.particleShaderGeo.dispose()
  }
}
